// ============================================================================
// components/mail/MailSidebar.tsx — левая колонка экрана «Почта»: ЯЩИКИ и ПАПКИ.
// Только представление: данные и счётчики приходят из lib/mail/mailData.
// ============================================================================

import { Folder, Inbox, Mail, Mails } from 'lucide-react';
import type { MailAccount, MailFolderView } from '@/lib/mail/mailData';
import { cn } from '@/lib/utils';

interface MailSidebarProps {
  accounts: MailAccount[];
  accountId: string;
  onSelectAccount: (accountId: string) => void;
  folders: MailFolderView[];
  folderKey: string;
  onSelectFolder: (folderKey: string) => void;
  loading: boolean;
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-auto min-w-5 rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold leading-5 text-primary-foreground">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function MailSidebar({
  accounts,
  accountId,
  onSelectAccount,
  folders,
  folderKey,
  onSelectFolder,
  loading,
}: MailSidebarProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-2">
      <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Ящики
      </div>
      {loading && accounts.length === 0 && (
        <div className="px-2 py-1 text-sm text-muted-foreground">Загрузка…</div>
      )}
      {!loading && accounts.length === 0 && (
        <div className="px-2 py-1 text-sm text-muted-foreground">Ящиков нет</div>
      )}
      {accounts.map((account) => {
        const active = account.id === accountId;
        return (
          <button
            key={account.id}
            type="button"
            onClick={() => onSelectAccount(account.id)}
            className={cn(
              'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
              active ? 'bg-primary/10 text-primary' : 'hover:bg-accent hover:text-accent-foreground',
            )}
            title={account.email || account.name}
          >
            <Mail className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{account.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {account.email || 'адрес не задан'}
                {!account.enabled && ' · выключен'}
              </span>
            </span>
            {!account.enabled && (
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-muted-foreground/50" title="Ящик выключен" />
            )}
            {account.enabled && (
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" title="Ящик включён" />
            )}
          </button>
        );
      })}

      <div className="mt-3 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Папки
      </div>
      <div className="px-2 pb-1 text-[11px] leading-tight text-muted-foreground/80">
        счётчики — по загруженным письмам
      </div>
      {folders.map((folder) => {
        const active = folder.key === folderKey;
        const Icon = folder.key === '' ? Mails : folder.key.toUpperCase() === 'INBOX' ? Inbox : Folder;
        return (
          <button
            key={folder.key || '__all__'}
            type="button"
            onClick={() => onSelectFolder(folder.key)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
              active ? 'bg-primary/10 text-primary' : 'hover:bg-accent hover:text-accent-foreground',
            )}
            title={folder.key || 'Все папки'}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{folder.label}</span>
            {folder.total > 0 && (
              <span className="text-xs text-muted-foreground">{folder.total}</span>
            )}
            <UnreadBadge count={folder.unread} />
          </button>
        );
      })}
    </div>
  );
}
